import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import { TaskState } from '~/services/cache/types'
import { useAppContext, type AppContextType } from '~/app-context'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { Button } from '~common/components/button'
import { LoadingIndicator } from '~common/components/loading'
import type { Theme } from '~common/theme/types'
import { LoginComponentLogic } from './logic'
import { ErrorBox } from '~/features/error-box'

export interface LoginPermissionsDependencies {
    onSuccess: (params: { newUser: boolean }) => void
    onError?: (error: string) => void
}

export type LoginPermissionsState = {
    loadState: TaskState
    error: string | null
}

export default function LoginPermissionsComponent(
    props: LoginPermissionsDependencies,
) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(LoginComponentLogic, {
        ...props,
        ...appContext,
    })

    const popupBlocked = () => {
        return (
            <PermissionScreenContainer>
                <PermissionsTitle>Popups Blocked</PermissionsTitle>

                <InfoBoxContainer>
                    <InfoBox>
                        <InfoText>
                            To continue with sign-in, please allow popups for
                            this site in your browser settings. Once allowed,
                            click the button below to recheck.
                        </InfoText>
                    </InfoBox>
                </InfoBoxContainer>
                <Button
                    onClick={logic.recheckPopupBlocked}
                    label="I've allowed popups"
                    size="medium"
                />
            </PermissionScreenContainer>
        )
    }

    const permissionScreen = () => {
        return (
            <PermissionScreenContainer>
                <PermissionsTitle>
                    About the permissions <br />
                    Taxing needs
                </PermissionsTitle>

                <InfoBoxContainer>
                    <InfoBox>
                        <InfoTitle>Gmail Read Access</InfoTitle>
                        <InfoText>
                            95% never touched, 5% processed with AI
                            <br />
                            and {'<'}1% backed up to the cloud.
                        </InfoText>
                    </InfoBox>
                    <InfoBox>
                        <InfoTitle>Google Drive File Access</InfoTitle>
                        <InfoText>
                            Taxing creates folders to save extracted PDFs.{' '}
                            <br />
                            We cannot read or modify anything else.
                        </InfoText>
                    </InfoBox>
                </InfoBoxContainer>
                <PrivacyDisclaimer
                    target="_blank"
                    href="https://taxing.app/privacy"
                >
                    Privacy Policy
                </PrivacyDisclaimer>
                <Button
                    onClick={logic.handleGoogleSignIn}
                    disabled={
                        state.loadState === 'running' || state.popupBlocked
                    }
                    label={
                        state.loadState === 'running' ? (
                            <LoadingIndicator size="small" />
                        ) : (
                            'Scan Gmail'
                        )
                    }
                    icon={state.loadState === 'running' ? undefined : 'gmail'}
                    size="large"
                    maxWidth="250px"
                    fullWidth
                    height="60px"
                />
                <InfoTextSmall>
                    Make sure to enable popups
                    <br />
                    for the login to work
                </InfoTextSmall>
            </PermissionScreenContainer>
        )
    }

    return (
        <Container>
            {state.error && <ErrorBox errorMessage={state.error} />}
            {state.popupBlocked ? popupBlocked() : permissionScreen()}
        </Container>
    )
}

const PrivacyDisclaimer = styled.a`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.lg};
    color: ${({ theme }) => (theme as Theme).colors.greyScale[100]};
    text-decoration: none;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${({ theme }) => (theme as Theme).spacing[8]};
    box-sizing: border-box;
    gap: ${({ theme }) => (theme as Theme).spacing[10]};
`

const PermissionsTitle = styled.h1`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize['3xl']};
    font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.bold};
    color: ${({ theme }) => (theme as Theme).colors.text.primary};
    text-align: center;
`

const ErrorMessage = styled.div`
    background-color: ${({ theme }) => (theme as Theme).colors.error[50]};
    color: ${({ theme }) => (theme as Theme).colors.error[700]};
    padding: ${({ theme }) => (theme as Theme).spacing[3]};
    border-radius: ${({ theme }) => (theme as Theme).borderRadius.md};
    margin-bottom: ${({ theme }) => (theme as Theme).spacing[4]};
    text-align: center;
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.sm};
`

const InfoBox = styled.div`
    background-color: ${({ theme }) => (theme as Theme).colors.greyScale[300]};
    padding: ${({ theme }) => (theme as Theme).spacing[4]};
    border-radius: ${({ theme }) => (theme as Theme).borderRadius.md};
    width: 100%;
    max-width: 400px;
    box-sizing: border-box;
`

const InfoTitle = styled.h3`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.lg};
    font-weight: ${({ theme }) =>
        (theme as Theme).typography.fontWeight.medium};
    color: ${({ theme }) => (theme as Theme).colors.text.primary};
    margin: 0 0 ${({ theme }) => (theme as Theme).spacing[2]} 0;
`

const InfoText = styled.p`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.sm};
    color: ${({ theme }) => (theme as Theme).colors.text.secondary};
    margin: 0;
    line-height: 1.5;
`

const InfoBoxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => (theme as Theme).spacing[4]};
    margin: -${({ theme }) => (theme as Theme).spacing[6]} 0
        ${({ theme }) => (theme as Theme).spacing[6]} 0;
`

const PopupWarning = styled.div`
    background-color: ${({ theme }) =>
        (theme as Theme).colors.warning[50] ||
        (theme as Theme).colors.greyScale[300]};
    color: ${({ theme }) =>
        (theme as Theme).colors.warning[700] ||
        (theme as Theme).colors.text.primary};
    padding: ${({ theme }) => (theme as Theme).spacing[3]};
    border-radius: ${({ theme }) => (theme as Theme).borderRadius.md};
    margin-bottom: ${({ theme }) => (theme as Theme).spacing[4]};
    text-align: center;
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.sm};
`

const WarningTitle = styled.h3`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.lg};
    font-weight: ${({ theme }) =>
        (theme as Theme).typography.fontWeight.medium};
    color: ${({ theme }) => (theme as Theme).colors.text.primary};
    margin: 0 0 ${({ theme }) => (theme as Theme).spacing[2]} 0;
`

const WarningText = styled.p`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.sm};
    color: ${({ theme }) => (theme as Theme).colors.text.secondary};
    margin: 0 0 ${({ theme }) => (theme as Theme).spacing[3]} 0;
    line-height: 1.5;
`

const RecheckButton = styled(Button)`
    margin-top: ${({ theme }) => (theme as Theme).spacing[2]};
`

const PermissionScreenContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => (theme as Theme).spacing[10]};
`

const InfoTextSmall = styled.p`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.sm};
    color: ${({ theme }) => (theme as Theme).colors.text.secondary};
    margin: 0;
    line-height: 1.5;
    text-align: center;
`
